/**
 * ImpactTabPanel — The public-facing story of a project's transformation.
 *
 * Sections:
 *   1. "What This Project Produces" — Read-only summary of mission outcomes.
 *   2. "What This Project Needs" + "Who's Needed" — Side-by-side needs grid.
 *
 * Campaign Alignment (SDG badges etc.) will return once the campaign
 * system is designed and built — see ADR-003.
 */

import React, { useMemo, useState, useCallback } from 'react';
import { PillarData } from '../../lib/pillarTypes';
import type { PreflightItem } from '../../Data/PreFlight/preflightTypes';
import type { MissionSubKpiResult, TargetDef } from '../../kpi-engine/mission';
import { preflightStore, storeKey } from '../../Data/store';
import { usePreflightLoader } from '../../hooks/usePreflightLoader';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import OpenRolesSection from '../../Components/ProjectTeam/OpenRolesSection';
import { excludeHse } from '../../Data/missionSubkpiFilter';
import styles from './ProjectDetail.module.css';
import impactStyles from './ImpactTabPanel.module.css';

const NEEDS_PREVIEW_COUNT = 3;

/** Indicator color → CSS token. Shared by main panel and OutcomeCard. */
const INDICATOR_COLOR: Record<number, string> = {
  0: 'var(--text-muted)',
  1: 'var(--color-error)',
  2: 'var(--color-warning)',
  3: 'var(--color-success)',
};

const CATEGORY_ICONS: Record<string, string> = {
  materials: '\u{1F4E6}',        // 📦
  equipment: '\u{1F527}',        // 🔧
  services: '\u{1F91D}',         // 🤝
  labor: '\u{1F477}',            // 👷
  transportation: '\u{1F68C}',   // 🚌
  facilities: '\u{1F3E2}',       // 🏢
  admin_legal: '\u{1F4CB}',      // 📋
  knowledge: '\u{1F4DA}',        // 📚
  financial: '\u{1F4B0}',        // 💰
  in_kind: '\u{1F381}',          // 🎁
  software_tech: '\u{1F4BB}',    // 💻
  other: '\u{1F4CE}',            // 📎
  // Legacy fallback keys for backward compatibility
  'tools_equipment': '\u{1F527}',
  'administrative': '\u{1F4CB}',
  'land_site': '\u{1F3E2}',
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export interface ImpactTabPanelProps {
  data: PillarData;
  projectGuid: string;
  currentUserGuid?: string;
  /** Enriched project row with mission data attached by App.tsx. */
  projectPayload?: any;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function ImpactTabPanel({ data, projectGuid, currentUserGuid, projectPayload }: ImpactTabPanelProps) {
  const { t } = useAppTranslation('pages');
  const { t: tKpi } = useAppTranslation('kpi');

  /* ── Public needs from pre-flight store ──────── */
  const { preflightVersion } = usePreflightLoader(projectGuid, true);

  const publicNeeds: PreflightItem[] = useMemo(() => {
    void preflightVersion;
    if (!projectGuid) return [];
    const key = storeKey(projectGuid);
    const items = preflightStore.get(key) || [];
    return items.filter((i) => i.visibility === 'public' && !i.is_fulfilled);
  }, [projectGuid, preflightVersion]);

  const allPublicItems: PreflightItem[] = useMemo(() => {
    void preflightVersion;
    if (!projectGuid) return [];
    const key = storeKey(projectGuid);
    const items = preflightStore.get(key) || [];
    return items.filter((i) => i.visibility === 'public');
  }, [projectGuid, preflightVersion]);

  const fulfilledPublicCount = allPublicItems.length - publicNeeds.length;
  const publicReadinessPct = allPublicItems.length > 0
    ? Math.round((fulfilledPublicCount / allPublicItems.length) * 100)
    : 0;

  const [needsExpanded, setNeedsExpanded] = useState(false);
  const toggleNeeds = useCallback(() => setNeedsExpanded((v) => !v), []);
  const visibleNeeds = needsExpanded
    ? publicNeeds
    : publicNeeds.slice(0, NEEDS_PREVIEW_COUNT);
  const hasMoreNeeds = publicNeeds.length > NEEDS_PREVIEW_COUNT;

  /* ── Mission data ───────────────────────────────────────────── */
  // DEV-822 spillover: HSE Incident Tracker sub-KPIs ride on the Mission
  // category for storage, but conceptually they aren't "outputs the project
  // produces" — they're a separate always-on safety surface. Filter them
  // out of "What This Project Produces" so HSE doesn't appear here with
  // a misleading "higher is better" framing.
  const missionSubkpis: MissionSubKpiResult[] = excludeHse(
    projectPayload?.mission_subkpis || [],
  );
  const missionTargets: Record<string, TargetDef> | null = projectPayload?.mission_targets || null;
  const missionScore: number | null = projectPayload?.mission_score_0_100 ?? null;
  const missionColor: number = projectPayload?.mission_indicator_color ?? 0;

  return (
    <div className={styles.panel}>
      {/* ============================================================ */}
      {/*  Section 1: What This Project Produces                       */}
      {/* ============================================================ */}
      <div className={impactStyles.section}>
        <div className={impactStyles.sectionHeader}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          <h3 className={impactStyles.sectionTitle}>{t('projectDetail.impact.whatProduces')}</h3>
          {missionScore != null && missionScore > 0 && (
            <span className={impactStyles.healthBadge} style={{ color: INDICATOR_COLOR[missionColor] }}>
              {t('projectDetail.impact.missionHealth')}: {Math.round(missionScore)}%
            </span>
          )}
        </div>

        {missionSubkpis.length === 0 ? (
          <div className={impactStyles.emptySection}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            <p className={impactStyles.emptyTitle}>{t('projectDetail.impact.noMissionData')}</p>
            <p className={impactStyles.emptyHint}>{t('projectDetail.impact.noMissionDataHint')}</p>
          </div>
        ) : (
          <div className={impactStyles.outcomesGrid}>
            {missionSubkpis.map((sub) => (
              <OutcomeCard
                key={sub.subKpiKey}
                sub={sub}
                target={missionTargets?.[sub.subKpiKey]}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      {/* ============================================================ */}
      {/*  Section 2: What's Needed + Who's Needed (side by side)       */}
      {/* ============================================================ */}
      <div className={styles.needsGrid}>
        {/* What's Needed (non-human resources) */}
        <div className={styles.needsStrip}>
          <div className={styles.needsHeader}>
            <h3 className={styles.needsTitle}>{tKpi('preflight.whatsNeeded')}</h3>
            {publicNeeds.length > 0 && (
              <span className={styles.needsCount}>
                {publicNeeds.length} {tKpi('preflight.needsRemaining')}
              </span>
            )}
          </div>

          {allPublicItems.length > 0 && (
            <div className={styles.needsProgressRow}>
              <div className={styles.needsBar}>
                <div
                  className={styles.needsBarFill}
                  style={{ width: `${publicReadinessPct}%` }}
                />
              </div>
              <span className={styles.needsBarLabel}>
                {fulfilledPublicCount}/{allPublicItems.length} {tKpi('preflight.fulfilled_label')}
              </span>
            </div>
          )}

          {publicNeeds.length === 0 ? (
            <div className={impactStyles.emptySection}>
              <p className={impactStyles.emptyTitle}>{t('projectDetail.impact.noPublicNeeds')}</p>
              <p className={impactStyles.emptyHint}>{t('projectDetail.impact.noPublicNeedsHint')}</p>
            </div>
          ) : (
            <>
              <div className={styles.needsList}>
                {visibleNeeds.map((item) => (
                  <div key={item.item_guid} className={styles.needItem}>
                    <div className={styles.needIcon}>{CATEGORY_ICONS[item.category]}</div>
                    <div className={styles.needContent}>
                      <span className={styles.needLabel}>{item.label}</span>
                      {item.requirement_type === 'threshold' && item.target_quantity != null && (
                        <span className={styles.needMeta}>
                          {item.current_quantity}/{item.target_quantity} {item.unit || ''}
                        </span>
                      )}
                      {item.description && (
                        <span className={styles.needDesc}>{item.description}</span>
                      )}
                    </div>
                    <span className={styles.needCategoryTag}>
                      {tKpi(`preflight.categories.${item.category}`)}
                    </span>
                  </div>
                ))}
              </div>

              {hasMoreNeeds && (
                <button
                  type="button"
                  className={styles.needsToggle}
                  onClick={toggleNeeds}
                >
                  {needsExpanded
                    ? tKpi('preflight.showLess')
                    : tKpi('preflight.showAll', { count: publicNeeds.length })}
                </button>
              )}
            </>
          )}
        </div>

        {/* Who's Needed (open public roles) */}
        {projectGuid && (
          <OpenRolesSection projectGuid={projectGuid} currentUserGuid={currentUserGuid || ''} />
        )}
      </div>

      {/* Campaign Alignment section removed — awaiting campaign system design (ADR-003). */}
    </div>
  );
}

/* ================================================================== */
/*  OutcomeCard — Mission subKPI outcome summary                       */
/* ================================================================== */

function OutcomeCard({
  sub,
  target,
  t,
}: {
  sub: MissionSubKpiResult;
  target?: TargetDef;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const key = sub.subKpiKey;
  /* Prefer the engine-provided label; fall back to humanized key. */
  const label = sub.label || key.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
  const latestValue = sub.latest_value;
  const goal = target?.goal ?? sub.target_goal ?? null;
  const unit = sub.uom ?? '';
  const direction = target?.direction ?? 'increase_is_good';
  const score = sub.score_0_100;
  const colorVal = sub.indicator_color_value ?? 0;

  const pct = goal != null && goal > 0
    ? Math.min(100, Math.round(((latestValue ?? 0) / goal) * 100))
    : null;

  const directionLabel =
    direction === 'increase_is_good' ? t('projectDetail.impact.directionUp')
    : direction === 'decrease_is_good' ? t('projectDetail.impact.directionDown')
    : t('projectDetail.impact.directionTarget');

  return (
    <div className={impactStyles.outcomeCard}>
      <div className={impactStyles.outcomeHeader}>
        <span className={impactStyles.outcomeLabel}>{label}</span>
        {score != null && (
          <span className={impactStyles.outcomeScore} style={{ color: INDICATOR_COLOR[colorVal] }}>
            {Math.round(score)}%
          </span>
        )}
      </div>

      <div className={impactStyles.outcomeBody}>
        {latestValue != null && (
          <span className={impactStyles.outcomeValue}>
            {typeof latestValue === 'number' ? latestValue.toLocaleString() : latestValue}
            {unit ? ` ${unit}` : ''}
          </span>
        )}

        {goal != null ? (
          <span className={impactStyles.outcomeGoal}>
            {t('projectDetail.impact.outcomeGoal')}: {goal.toLocaleString()}{unit ? ` ${unit}` : ''}
          </span>
        ) : (
          <span className={impactStyles.outcomeTracking}>
            {t('projectDetail.impact.outcomeNoGoal')}
          </span>
        )}
      </div>

      {/* Progress bar */}
      {pct != null && (
        <div className={impactStyles.outcomeProgress}>
          <div className={impactStyles.outcomeProgressBar}>
            <div
              className={impactStyles.outcomeProgressFill}
              style={{
                width: `${pct}%`,
                background: INDICATOR_COLOR[colorVal] || 'var(--color-warning)',
              }}
            />
          </div>
        </div>
      )}

      <span className={impactStyles.outcomeDirection}>{directionLabel}</span>
    </div>
  );
}

/* CampaignCard removed — campaign system not yet designed (see ADR-003). */
